import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const TOOLTIP_WIDTH = 288; // w-72
const GAP = 8;
const VIEWPORT_PADDING = 8;

interface Position {
  top: number;
  left: number;
}

interface InfoTooltipProps {
  /** The explanation shown in the bubble. */
  text: string;
  /** Accessible name of the trigger, e.g. "Erklärung: Offen". */
  label?: string;
  /** Extra classes for the icon — pass a text colour to match the surroundings. */
  className?: string;
}

/**
 * Info icon (Heroicons outline "information-circle") that reveals a short
 * explanation on hover or keyboard focus.
 *
 * The bubble is rendered into a portal with `position: fixed` because the
 * places that use it sit inside scroll containers (the Kanban row is
 * `overflow-x-auto`) and inside a dnd-kit `DndContext`, whose overlay
 * transforms would otherwise become the containing block.
 */
export function InfoTooltip({ text, label = 'Erklärung', className = '' }: InfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<Position>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();

  const calculatePosition = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const bubbleHeight = bubbleRef.current?.offsetHeight ?? 96;

    let top = rect.bottom + GAP;
    // Flip above when there is no room below
    if (top + bubbleHeight > window.innerHeight - VIEWPORT_PADDING) {
      top = Math.max(VIEWPORT_PADDING, rect.top - bubbleHeight - GAP);
    }

    // Centre on the icon, then clamp to the viewport
    let left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
    left = Math.max(
      VIEWPORT_PADDING,
      Math.min(left, window.innerWidth - TOOLTIP_WIDTH - VIEWPORT_PADDING),
    );

    setPosition({ top, left });
  };

  const show = () => {
    calculatePosition();
    setOpen(true);
  };

  // Measure once the bubble is in the DOM — the first pass guesses its height.
  useEffect(() => {
    if (!open) return;
    calculatePosition();
  }, [open, text]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // Scrolling would leave the fixed bubble behind; closing is the honest answer.
    const handleScroll = () => setOpen(false);

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', calculatePosition);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-describedby={open ? tooltipId : undefined}
        onMouseEnter={show}
        onMouseLeave={() => setOpen(false)}
        onFocus={show}
        onBlur={() => setOpen(false)}
        // Swallow the click: triggers often sit inside a clickable header.
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        // `relative` is load-bearing: the sr-only span below is `position: absolute`,
        // and without a positioned trigger its containing block is far up the tree.
        // It would then escape a `position: static` scroll container (the Kanban row)
        // and stretch the *document's* scroll width out to the off-screen columns.
        className={`relative inline-flex items-center cursor-help opacity-60 hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-500 rounded-full ${className}`}
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span className="sr-only">{text}</span>
      </button>

      {open &&
        createPortal(
          <div
            ref={bubbleRef}
            id={tooltipId}
            role="tooltip"
            className="fixed z-50 px-3 py-2 bg-gray-900 text-white text-xs leading-relaxed rounded-md shadow-lg pointer-events-none"
            style={{ top: position.top, left: position.left, width: TOOLTIP_WIDTH }}
          >
            {text}
          </div>,
          document.body,
        )}
    </>
  );
}

export default InfoTooltip;
